import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import {
  approveCorrectionRequest,
  createCorrectionRequest,
  listMyCorrectionRequests,
  listTeamCorrectionRequests,
  rejectCorrectionRequest,
} from "../api/correctionRequests";
import type { CreateCorrectionRequestInput } from "../api/types";

const ANOMALIES_KEY = ["tawa", "anomalies"] as const;
const MY_REQUESTS_KEY = [...ANOMALIES_KEY, "correction-requests", "mine"];
const TEAM_REQUESTS_KEY = [...ANOMALIES_KEY, "correction-requests", "team"];

export function useMyCorrectionRequests(enabled = true) {
  return useQuery({
    queryKey: MY_REQUESTS_KEY,
    queryFn: listMyCorrectionRequests,
    enabled,
    staleTime: 60_000,
  });
}

export function useTeamCorrectionRequests(enabled = true) {
  return useQuery({
    queryKey: TEAM_REQUESTS_KEY,
    queryFn: listTeamCorrectionRequests,
    enabled,
    staleTime: 30_000,
  });
}

function invalidateAnomalyReads(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ANOMALIES_KEY });
  void qc.invalidateQueries({ queryKey: ["tawa", "manager", "team"] });
}

export function useSubmitCorrectionRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCorrectionRequestInput) =>
      createCorrectionRequest(input),
    onSuccess: () => invalidateAnomalyReads(qc),
  });
}

export function useApproveCorrectionRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => approveCorrectionRequest(id),
    onSuccess: () => {
      invalidateAnomalyReads(qc);
      // L'approbation crée un event (attendance_out ou manual_adjustment) :
      // timeline, sessions et feuilles changent.
      void qc.invalidateQueries({ queryKey: ["tawa", "worker"] });
      void qc.invalidateQueries({ queryKey: ["tawa", "manager"] });
      void qc.invalidateQueries({ queryKey: ["tawa", "sessions", "open"] });
    },
  });
}

export function useRejectCorrectionRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectCorrectionRequest(id, reason),
    onSuccess: () => invalidateAnomalyReads(qc),
  });
}
